/**
 * Versioned save game in localStorage with migrations and a safe fallback.
 * - Corrupt JSON is backed up to `${key}.corrupt` and replaced with defaults.
 * - No localStorage (private mode, node) → in-memory store; the game still works.
 */
import { DEFAULT_SETTINGS, sanitizeSettings, type Settings } from './settings';
import { newCareer, sanitizeCareer, type CareerState, type Difficulty } from './career';
import type { DailyBest } from './daily';
import { UNLOCKS } from './unlocks';

export const SAVE_KEY = 'sports-director.save';
export const SAVE_VERSION = 2;

export interface LifetimeStats {
  matchesPlayed: number;
  bestRating: number;
  bestStreak: number;
  peakViewers: number;
  totalPoints: number;
}

export interface QuickMatchPrefs { difficulty: Difficulty; halfLengthSeconds: number }

export interface SaveData {
  version: typeof SAVE_VERSION;
  settings: Settings;
  daily: Record<string, DailyBest>;
  career: CareerState;
  unlocks: string[];
  stats: LifetimeStats;
  quick: QuickMatchPrefs;
  /** Has the player seen How to Play? */
  seenTutorial: boolean;
  /** Guided training match ("The First Broadcast"). */
  training: TrainingProgress;
}

export interface TrainingProgress {
  /** Finished the training match at least once. */
  done: boolean;
  /** Skipped it (title stops pushing it as the default focus). */
  skipped: boolean;
  /** Best training letter, if completed. */
  letter?: string;
}

export interface KVStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function memoryStore(initial: Record<string, string> = {}): KVStore & { dump(): Record<string, string> } {
  const m = new Map(Object.entries(initial));
  return {
    getItem: (k) => (m.has(k) ? m.get(k)! : null),
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
    dump: () => Object.fromEntries(m),
  };
}

function randomSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}

export function defaultSave(careerSeed: number = randomSeed()): SaveData {
  return {
    version: SAVE_VERSION,
    settings: { ...DEFAULT_SETTINGS },
    daily: {},
    career: newCareer(careerSeed),
    unlocks: [],
    stats: { matchesPlayed: 0, bestRating: 0, bestStreak: 0, peakViewers: 0, totalPoints: 0 },
    quick: { difficulty: 'pro', halfLengthSeconds: 240 },
    seenTutorial: false,
    training: { done: false, skipped: false },
  };
}

type Json = Record<string, unknown>;

/**
 * Migration steps keyed by the version they upgrade FROM.
 * v1 (prototype): { v: 1, volume: number, reducedMotion?: bool, dailyBest: { [date]: { rating, letter } } }
 */
const MIGRATIONS: Record<number, (d: Json) => Json> = {
  1: (d) => {
    const dailyBest = (d.dailyBest && typeof d.dailyBest === 'object' ? d.dailyBest : {}) as Record<string, Json>;
    const daily: Record<string, Json> = {};
    for (const [key, b] of Object.entries(dailyBest)) {
      if (!b || typeof b.rating !== 'number') continue;
      daily[key] = {
        dateKey: key, dailyNumber: 0, seed: 0, rating: b.rating, letter: b.letter ?? 'F', total: 0,
        peakViewers: 0, goalsCovered: 0, goals: 0, attempts: 1, playedAt: '',
      };
    }
    return { version: 2, settings: { masterVolume: d.volume, reducedMotion: d.reducedMotion }, daily };
  },
};

function versionOf(d: Json): number {
  if (typeof d.version === 'number') return d.version;
  if (typeof d.v === 'number') return d.v;
  return 1;
}

/** Upgrade any historical save shape to the current SaveData, filling gaps with defaults. */
export function migrateSave(raw: unknown, fallbackSeed: number = randomSeed()): SaveData {
  const base = defaultSave(fallbackSeed);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  let d = raw as Json;
  let v = versionOf(d);
  if (v > SAVE_VERSION) return base; // from the future: don't guess
  while (v < SAVE_VERSION) {
    const step = MIGRATIONS[v];
    if (!step) return base;
    d = step(d);
    v = versionOf(d);
  }
  const stats = (d.stats && typeof d.stats === 'object' ? d.stats : {}) as Partial<LifetimeStats>;
  const n = (x: unknown, def: number) => (typeof x === 'number' && Number.isFinite(x) ? x : def);
  const quick = (d.quick && typeof d.quick === 'object' ? d.quick : {}) as Partial<QuickMatchPrefs>;
  const validUnlocks = new Set(UNLOCKS.map((u) => u.id as string));
  return {
    version: SAVE_VERSION,
    settings: sanitizeSettings(d.settings),
    daily: d.daily && typeof d.daily === 'object' ? (d.daily as Record<string, DailyBest>) : {},
    career: sanitizeCareer(d.career, fallbackSeed),
    unlocks: Array.isArray(d.unlocks) ? (d.unlocks as unknown[]).filter((u): u is string => typeof u === 'string' && validUnlocks.has(u)) : [],
    stats: {
      matchesPlayed: n(stats.matchesPlayed, 0), bestRating: n(stats.bestRating, 0), bestStreak: n(stats.bestStreak, 0),
      peakViewers: n(stats.peakViewers, 0), totalPoints: n(stats.totalPoints, 0),
    },
    quick: {
      difficulty: quick.difficulty === 'rookie' || quick.difficulty === 'legend' ? quick.difficulty : 'pro',
      halfLengthSeconds: [180, 240, 360].includes(quick.halfLengthSeconds as number) ? (quick.halfLengthSeconds as number) : 240,
    },
    seenTutorial: d.seenTutorial === true,
    training: sanitizeTraining(d.training),
  };
}

function sanitizeTraining(raw: unknown): TrainingProgress {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out: TrainingProgress = { done: r.done === true, skipped: r.skipped === true };
  if (typeof r.letter === 'string') out.letter = r.letter;
  return out;
}

export interface SaveStore {
  get(): Readonly<SaveData>;
  /** Mutate-or-return update; persisted immediately. */
  update(fn: (d: SaveData) => SaveData | void): SaveData;
  reset(): SaveData;
  /** False when falling back to memory (no localStorage). */
  readonly persistent: boolean;
}

function detectBackend(): { store: KVStore; persistent: boolean } {
  try {
    const ls = (globalThis as { localStorage?: KVStore }).localStorage;
    if (ls) {
      const probe = '__sd_probe__';
      ls.setItem(probe, '1');
      ls.removeItem(probe);
      return { store: ls, persistent: true };
    }
  } catch { /* blocked storage */ }
  return { store: memoryStore(), persistent: false };
}

export function createSaveStore(opts: { backend?: KVStore; key?: string; careerSeed?: number } = {}): SaveStore {
  const key = opts.key ?? SAVE_KEY;
  const { store, persistent } = opts.backend ? { store: opts.backend, persistent: true } : detectBackend();
  let data: SaveData;

  const raw = safeGet(store, key);
  if (raw == null) {
    data = defaultSave(opts.careerSeed);
  } else {
    try {
      data = migrateSave(JSON.parse(raw), opts.careerSeed);
    } catch {
      safeSet(store, `${key}.corrupt`, raw);
      data = defaultSave(opts.careerSeed);
    }
  }
  safeSet(store, key, JSON.stringify(data));

  return {
    persistent,
    get: () => data,
    update(fn) {
      const draft = structuredClone(data);
      data = fn(draft) ?? draft;
      safeSet(store, key, JSON.stringify(data));
      return data;
    },
    reset() {
      data = defaultSave(opts.careerSeed);
      safeSet(store, key, JSON.stringify(data));
      return data;
    },
  };
}

function safeGet(s: KVStore, k: string): string | null {
  try { return s.getItem(k); } catch { return null; }
}
function safeSet(s: KVStore, k: string, v: string): void {
  try { s.setItem(k, v); } catch { /* quota / blocked: keep in-memory copy */ }
}
